import { makeObservable, observable, runInAction, toJS } from 'mobx';
import { AuthSupplier } from '../../auth/auth.store';
import { logger } from '../../logging.service';
import { storageService } from '../../storage/storage.service';
import { UrlStore } from '../../url/url.store';
import { replaceStringParameters } from '../../utils/string-utils';
import { LabelKey } from './label-keys';
import { localizationSvc } from './localization.service';

export type TranslateFun = (
  key: LabelKey | string | undefined,
  parameters?: Record<string, string> | undefined,
  defaultDevStr?: string
) => string;

const LABEL_STORAGE_KEY = 'labels';

export class LocalizationStore {
  public labels: Record<string, string> = {};

  private loadedLabels: Set<string> = new Set();

  constructor(private urlStore: UrlStore) {
    makeObservable(this, {
      labels: observable,
    });

    this.restoreLabels();
  }

  public translate: TranslateFun = (
    key: LabelKey | string | undefined,
    parameters: Record<string, string> | undefined,
    defaultDevStr?: string
  ) => {
    const debugShowLabels = !!this.urlStore?.searchParams.devShowLabels;

    if (!key) {
      logger.warn('Undefined translate key is used!');
      return '';
    }

    const translatedText = this.getTranslatedLabel(key, parameters);
    if (debugShowLabels) {
      return `${key} (${translatedText})`;
    }

    if (defaultDevStr && translatedText === '') {
      return defaultDevStr;
    }

    return translatedText;
  };

  public translateResponse: TranslateFun = (
    key?: LabelKey | string | undefined,
    parameters?: Record<string, string> | undefined,
    defaultDevStr?: string
  ) => {
    const debugShowLabels = !!this.urlStore?.searchParams.devShowLabels;
    const translatedText = key ? this.getTranslatedLabel(key, parameters) : '';
    if (debugShowLabels && key) {
      return `${key} (${translatedText})`;
    }

    if (defaultDevStr && translatedText === '') {
      return defaultDevStr;
    }

    return translatedText;
  };

  public async loadLabels(moduleName: string, admin: boolean, authSupplier: AuthSupplier) {
    if (moduleName === 'admin') return;
    const { moduleId } = await authSupplier();
    const loadedModuleName = moduleId + '_' + moduleName;
    if (this.loadedLabels.has(loadedModuleName)) {
      logger.log(`Skipping label fetching, because ${moduleName} labels are loaded for ${moduleId}`);
      return;
    } else {
      this.loadedLabels.add(loadedModuleName);
    }
    await this.load(moduleName, admin, authSupplier);
  }

  public async loadLabelsByModuleName(moduleName: string, admin: boolean, authSupplier: AuthSupplier) {
    const moduleId = 'module_' + moduleName;
    if (this.loadedLabels.has(moduleId)) {
      logger.log(`Skipping label fetching, because ${moduleName} labels are loaded for ${moduleId}`);
      return;
    } else {
      this.loadedLabels.add(moduleId);
    }
    await this.load(moduleName, admin, authSupplier);
  }

  private async load(moduleName: string, admin: boolean, authSupplier: AuthSupplier) {
    const newLabels = await localizationSvc.loadLabels(moduleName, admin, authSupplier);
    runInAction(() => {
      this.addLabels(newLabels);
      this.storeLabels();
    });
  }

  private addLabels(newLabels: Record<string, string>) {
    this.labels = {
      ...toJS(this.labels),
      ...newLabels,
    };
  }

  private getTranslatedLabel(key: LabelKey | string, parameters: Record<string, string> | undefined) {
    const initialTemplate = this.labels[key];

    if (!initialTemplate) {
      return '';
    }

    if (!parameters) {
      return initialTemplate;
    }
    return replaceStringParameters(initialTemplate, parameters);
  }

  private storeLabels() {
    storageService.setItem(LABEL_STORAGE_KEY, toJS(this.labels), 'session');
  }

  private restoreLabels() {
    const storedLabels = storageService.getItem<LocalizationStore['labels']>(LABEL_STORAGE_KEY, 'session');
    this.addLabels(storedLabels ?? {});
  }
}
